import { SolicitationData, SolicitationType } from '../types/solicitation';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { collection, doc, setDoc, getDoc } from 'firebase/firestore';
import { validateSAMUrl, extractOpportunityId, getNoticeType } from '../utils/urlValidation';

export class SolicitationService {
  private functions = getFunctions(undefined, 'us-central1');
  private static instance: SolicitationService;

  private constructor() {}

  public static getInstance(): SolicitationService {
    if (!SolicitationService.instance) {
      SolicitationService.instance = new SolicitationService();
    }
    return SolicitationService.instance;
  }

  /**
   * Validates if a URL is a valid SAM.gov opportunity URL
   */
  public isValidSAMUrl(url: string): boolean {
    const validation = validateSAMUrl(url);
    return validation.isValid;
  }

  /**
   * Generates an AI summary for the solicitation text
   */
  private async generateAISummary(data: SolicitationData): Promise<string> {
    const generateSummary = httpsCallable<
      { text: string; noticeType: SolicitationType },
      { summary: string }
    >(this.functions, 'generateSummary');

    try {
      const textToAnalyze = [
        data.description,
        data.technicalDetails.projectPurpose,
        ...data.technicalDetails.requirements,
      ].join('\n\n');

      const result = await generateSummary({
        text: textToAnalyze,
        noticeType: data.noticeType
      });
      return result.data.summary;
    } catch (error) {
      console.error('Error generating AI summary:', error);
      return 'AI summary generation failed. Please try again later.';
    }
  }

  /**
   * Checks if the user has an active subscription
   */
  private async checkSubscription(userId: string): Promise<boolean> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return false;
      }

      const data = userDoc.data();
      const subscription = data.subscription;
      
      if (!subscription || subscription.status !== 'active') {
        return false;
      }

      const endDate = new Date(subscription.endDate);
      return endDate > new Date();
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  }

  public async fetchSolicitationData(userId: string, url: string): Promise<SolicitationData> {
    // Check subscription first
    const hasActiveSubscription = await this.checkSubscription(userId);
    if (!hasActiveSubscription) {
      throw new Error('Active subscription required');
    }

    // Validate URL
    const validation = validateSAMUrl(url);
    if (!validation.isValid) {
      throw new Error(validation.message || 'Invalid SAM.gov URL');
    }

    const extractSolicitationData = httpsCallable<
      { url: string; opportunityId: string; noticeType?: string },
      SolicitationData
    >(this.functions, 'extractSolicitationData');

    try {
      const opportunityId = extractOpportunityId(url);
      const noticeType = getNoticeType(url);

      if (!opportunityId) {
        throw new Error('Could not extract opportunity ID from URL');
      }

      // Check if we already have this solicitation in Firestore
      const existingData = await this.getSavedSolicitation(userId, opportunityId);
      if (existingData) {
        // If it's less than 24 hours old, return cached data
        const isCacheValid =
          new Date().getTime() - existingData.updatedAt.getTime() < 24 * 60 * 60 * 1000;
        if (isCacheValid) {
          return existingData;
        }
      }

      // Fetch fresh data from SAM.gov
      const result = await extractSolicitationData({
        url,
        opportunityId,
        noticeType: noticeType || undefined
      });

      const data = result.data;

      // Convert date strings to Date objects
      const processedData: SolicitationData = {
        ...data,
        timeline: {
          ...data.timeline,
          postedDate: new Date(data.timeline.postedDate),
          responseDate: new Date(data.timeline.responseDate),
          archiveDate: data.timeline.archiveDate ? new Date(data.timeline.archiveDate) : undefined,
          importantDates: data.timeline.importantDates.map(date => ({
            ...date,
            date: new Date(date.date)
          }))
        },
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt),
        archivedAt: data.archivedAt ? new Date(data.archivedAt) : undefined
      };

      // Save to Firestore
      await this.saveSolicitationData(userId, processedData);

      return processedData;
    } catch (error) {
      console.error('Error fetching solicitation data:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to fetch solicitation data: ${error.message}`);
      }
      throw new Error('Failed to fetch solicitation data');
    }
  }

  /**
   * Validates if a date is valid and can be converted to ISO string
   */
  private isValidDate(date: Date | undefined): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Safely converts a date to ISO string, returns null if date is invalid
   */
  private safeToISOString(date: Date | undefined): string | null {
    if (!this.isValidDate(date)) {
      console.warn('Invalid date detected:', date);
      return null;
    }
    try {
      return date!.toISOString();
    } catch (error) {
      console.error('Error converting date to ISO string:', error);
      return null;
    }
  }

  /**
   * Saves solicitation data to Firestore under the user's document
   */
  public async saveSolicitationData(userId: string, data: SolicitationData): Promise<void> {
    try {
      // Validate critical dates
      if (!this.isValidDate(data.timeline.postedDate)) {
        console.error('Invalid posted date:', data.timeline.postedDate);
        data.timeline.postedDate = new Date();
      }
      if (!this.isValidDate(data.timeline.responseDate)) {
        console.error('Invalid response date:', data.timeline.responseDate);
        const oneMonthFromNow = new Date();
        oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
        data.timeline.responseDate = oneMonthFromNow;
      }

      const userSolicitationsRef = collection(db, 'users', userId, 'solicitations');
      await setDoc(doc(userSolicitationsRef, data.id), {
        ...data,
        timeline: {
          ...data.timeline,
          postedDate: data.timeline.postedDate.toISOString(),
          responseDate: data.timeline.responseDate.toISOString(),
          archiveDate: this.safeToISOString(data.timeline.archiveDate),
          importantDates: data.timeline.importantDates
            .filter(date => this.isValidDate(date.date))
            .map(date => ({
              ...date,
              date: date.date.toISOString()
            }))
        },
        createdAt: this.isValidDate(data.createdAt) ? data.createdAt.toISOString() : new Date().toISOString(),
        updatedAt: this.isValidDate(data.updatedAt) ? data.updatedAt.toISOString() : new Date().toISOString(),
        archivedAt: this.safeToISOString(data.archivedAt)
      });
    } catch (error) {
      console.error('Error saving solicitation data:', error);
      throw new Error('Failed to save solicitation data');
    }
  }

  /**
   * Gets saved solicitation data from Firestore from the user's document
   */
  public async getSavedSolicitation(userId: string, id: string): Promise<SolicitationData | null> {
    try {
      const docRef = doc(db, 'users', userId, 'solicitations', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data() as SolicitationData;

      // Convert ISO date strings back to Date objects
      return {
        ...data,
        timeline: {
          ...data.timeline,
          postedDate: new Date(data.timeline.postedDate),
          responseDate: new Date(data.timeline.responseDate),
          archiveDate: data.timeline.archiveDate ? new Date(data.timeline.archiveDate) : undefined,
          importantDates: data.timeline.importantDates.map(date => ({
            ...date,
            date: new Date(date.date)
          }))
        },
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt),
        archivedAt: data.archivedAt ? new Date(data.archivedAt) : undefined
      };
    } catch (error) {
      console.error('Error getting solicitation data:', error);
      throw new Error('Failed to get solicitation data');
    }
  }
}

export const solicitationService = SolicitationService.getInstance();
